import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Badge,
  Dropdown,
  Option,
  Tooltip,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
  Checkbox,
  tokens,
} from '@fluentui/react-components';
import {
  BuildingRegular,
  PersonRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
  CalendarRegular,
  DocumentCheckmarkRegular,
  MailRegular,
  ArrowClockwiseRegular,
  OpenRegular,
  SendRegular,
  CheckboxCheckedRegular,
  CheckboxUncheckedRegular,
} from '@fluentui/react-icons';
import { format } from 'date-fns';
import { getInHouseClientStatus } from '../services/api';
import { realLinkProps } from '../components/links/realLinkProps';
import { TableSkeleton } from '../components/LoadingSkeleton';
import {
  type InHouseClient,
  type InHouseContact,
  type TaxPrepStatus,
  inHouseServiceDisplayNames,
  contactRelationshipDisplayNames,
} from '../types';
import { getDataverseRecordUrl, isDataverseConfigured } from '../utils/dataverse';
import { useInHouseClientsScopeParams } from './useInHouseClientsScopeParams';

const currentYear = new Date().getFullYear();

export default function InHouseClientsPage() {
  const navigate = useNavigate();
  const {
    taxYear: selectedTaxYear,
    filterStatus,
    setTaxYear: setSelectedTaxYear,
    setFilterStatus,
  } = useInHouseClientsScopeParams();
  const [selectedClients, setSelectedClients] = useState<Set<string>>(new Set());

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['inHouseClientStatus', selectedTaxYear],
    queryFn: () => getInHouseClientStatus(selectedTaxYear),
  });

  const filteredClients = useMemo(() => {
    return data?.clients.filter(client => {
      if (filterStatus === 'all') return true;

      const clientMissing = !client.accountTaxPrepStatus.isSetUp ||
        client.relatedContacts.some(c => !c.taxPrepStatus.isSetUp);

      if (filterStatus === 'missing') return clientMissing;
      if (filterStatus === 'setup') return !clientMissing;
      return true;
    }) || [];
  }, [data?.clients, filterStatus]);

  // Get currently visible client IDs for select all functionality
  const visibleClientIds = useMemo(() => {
    return new Set(filteredClients.map(c => c.accountId));
  }, [filteredClients]);

  // Check if all visible clients are selected
  const allVisibleSelected = visibleClientIds.size > 0 &&
    Array.from(visibleClientIds).every(id => selectedClients.has(id));

  // Toggle selection for a single client
  const toggleClientSelection = (clientId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedClients(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) {
        next.delete(clientId);
      } else {
        next.add(clientId);
      }
      return next;
    });
  };

  // Select or deselect all visible clients
  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      // Deselect all visible
      setSelectedClients(prev => {
        const next = new Set(prev);
        visibleClientIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      // Select all visible
      setSelectedClients(prev => {
        const next = new Set(prev);
        visibleClientIds.forEach(id => next.add(id));
        return next;
      });
    }
  };

  // Real links (fleet ruling 2026-10-29): undefined with nothing selected, so the button below
  // falls back to a disabled plain button instead of a link to nowhere.
  const bulkOutreachHref = selectedClients.size > 0
    ? `/outreach?clientIds=${Array.from(selectedClients).join(',')}`
    : undefined;

  const renderStatusBadge = (status: TaxPrepStatus) => {
    if (status.isSetUp) {
      return (
        <Badge appearance="filled" color="success" icon={<CheckmarkCircleRegular />}>
          Set Up
        </Badge>
      );
    }
    return (
      <Badge appearance="filled" color="danger" icon={<DismissCircleRegular />}>
        Needs Attention
      </Badge>
    );
  };

  const renderStatusDetails = (status: TaxPrepStatus) => {
    return (
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '8px' }}>
        <Tooltip
          content={status.hasAppointment
            ? `Appointment: ${status.appointmentDate ? format(new Date(status.appointmentDate), 'MMM d, yyyy') : 'Scheduled'}`
            : 'No appointment scheduled'}
          relationship="label"
        >
          <Badge
            appearance={status.hasAppointment ? 'filled' : 'outline'}
            color={status.hasAppointment ? 'success' : 'danger'}
            icon={<CalendarRegular />}
            style={{ cursor: 'help' }}
          >
            Appointment
          </Badge>
        </Tooltip>

        <Tooltip
          content={status.hasChecklist
            ? `Checklist: ${status.checklistMatchStatus || 'Created'}`
            : 'No checklist created'}
          relationship="label"
        >
          <Badge
            appearance={status.hasChecklist ? 'filled' : 'outline'}
            color={status.hasChecklist ? 'success' : 'danger'}
            icon={<DocumentCheckmarkRegular />}
            style={{ cursor: 'help' }}
          >
            Checklist
          </Badge>
        </Tooltip>

        <Tooltip
          content={status.hasOutreach
            ? `Last outreach: ${status.lastOutreachDate ? format(new Date(status.lastOutreachDate), 'MMM d, yyyy') : 'Sent'}`
            : 'No outreach sent'}
          relationship="label"
        >
          <Badge
            appearance={status.hasOutreach ? 'filled' : 'outline'}
            color={status.hasOutreach ? 'success' : 'warning'}
            icon={<MailRegular />}
            style={{ cursor: 'help' }}
          >
            Outreach
          </Badge>
        </Tooltip>

        {status.taxReturnId && (
          <Tooltip content={`Tax Return: ${status.taxReturnStatus || 'Created'}`} relationship="label">
            <a
              {...realLinkProps(`/tax-returns/${status.taxReturnId}`, { navigate })}
              style={{ textDecoration: 'none' }}
            >
              <Badge appearance="tint" color="brand" style={{ cursor: 'pointer' }}>
                View Return
              </Badge>
            </a>
          </Tooltip>
        )}
      </div>
    );
  };

  const renderContact = (contact: InHouseContact) => {
    return (
      <div
        key={contact.contactId}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          padding: '12px',
          backgroundColor: contact.taxPrepStatus.isSetUp ? '#f0fdf4' : '#fef2f2',
          borderRadius: '6px',
          marginBottom: '8px',
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <PersonRegular />
            <span style={{ fontWeight: 500 }}>{contact.contactName}</span>
            <Badge appearance="outline" size="small">
              {contactRelationshipDisplayNames[contact.relationship]}
            </Badge>
            {renderStatusBadge(contact.taxPrepStatus)}
          </div>
          <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)', marginLeft: '24px' }}>
            {contact.email && <span>{contact.email}</span>}
            {contact.email && contact.phone && <span> • </span>}
            {contact.phone && <span>{contact.phone}</span>}
          </div>
          {renderStatusDetails(contact.taxPrepStatus)}
        </div>
        <div style={{ display: 'flex', gap: '4px' }}>
          {!contact.taxPrepStatus.isSetUp && (
            <Tooltip content="Send to Outreach" relationship="label">
              <Button
                as="a"
                appearance="subtle"
                icon={<MailRegular />}
                size="small"
                {...realLinkProps(`/outreach?clientId=${contact.contactId}`, { navigate })}
              />
            </Tooltip>
          )}
          {isDataverseConfigured() && (
            <Tooltip content="Open in Dataverse" relationship="label">
              <Button
                as="a"
                appearance="subtle"
                icon={<OpenRegular />}
                size="small"
                href={getDataverseRecordUrl('contact', contact.contactId) ?? undefined}
                target="_blank"
                rel="noopener noreferrer"
              />
            </Tooltip>
          )}
        </div>
      </div>
    );
  };

  const renderClient = (client: InHouseClient) => {
    const hasMissing = !client.accountTaxPrepStatus.isSetUp ||
      client.relatedContacts.some(c => !c.taxPrepStatus.isSetUp);
    const missingCount = client.relatedContacts.filter(c => !c.taxPrepStatus.isSetUp).length +
      (!client.accountTaxPrepStatus.isSetUp ? 1 : 0);
    const isSelected = selectedClients.has(client.accountId);

    // Get the first owner name from the service owners
    const ownerName = client.serviceOwners ? Object.values(client.serviceOwners).find(name => name) : null;

    return (
      <AccordionItem key={client.accountId} value={client.accountId}>
        <AccordionHeader
          expandIconPosition="end"
          style={{
            backgroundColor: hasMissing ? '#fef2f2' : '#f0fdf4',
            borderRadius: '8px',
            marginBottom: '4px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', padding: '8px 0' }}>
            <Checkbox
              checked={isSelected}
              onClick={(e) => toggleClientSelection(client.accountId, e)}
              style={{ marginRight: '4px' }}
            />
            <BuildingRegular style={{ fontSize: tokens.fontSizeBase500 }} />
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600 }}>{client.accountName}</span>
                {client.inHouseServices.map(service => (
                  <Badge key={service} appearance="tint" color="brand" size="small">
                    {inHouseServiceDisplayNames[service]}
                  </Badge>
                ))}
                {hasMissing ? (
                  <Badge appearance="filled" color="danger" size="small">
                    {missingCount} need attention
                  </Badge>
                ) : (
                  <Badge appearance="filled" color="success" size="small" icon={<CheckmarkCircleRegular />}>
                    All set up
                  </Badge>
                )}
                {client.accountTaxPrepStatus.appointmentDate && (
                  <Badge appearance="outline" size="small" icon={<CalendarRegular />}>
                    {format(new Date(client.accountTaxPrepStatus.appointmentDate), 'MMM d')}
                  </Badge>
                )}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>
                {ownerName && <span style={{ fontWeight: 500 }}>{ownerName}</span>}
                {ownerName && (client.accountEmail || client.accountPhone) && ' • '}
                {client.accountEmail}
                {client.accountEmail && client.accountPhone && ' • '}
                {client.accountPhone}
                {' • '}
                {client.relatedContacts.length} contact{client.relatedContacts.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </AccordionHeader>
        <AccordionPanel>
          <div style={{ padding: '16px', backgroundColor: 'var(--tl-bg-page)', borderRadius: '0 0 8px 8px' }}>
            {/* Account Tax Prep Status */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontWeight: 500, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <BuildingRegular />
                Business Return Status
                {renderStatusBadge(client.accountTaxPrepStatus)}
              </div>
              {renderStatusDetails(client.accountTaxPrepStatus)}
              {!client.accountTaxPrepStatus.isSetUp && (
                <div style={{ marginTop: '8px' }}>
                  <Button
                    as="a"
                    appearance="primary"
                    size="small"
                    icon={<MailRegular />}
                    {...realLinkProps(`/outreach?clientId=${client.accountId}`, { navigate })}
                  >
                    Send Outreach
                  </Button>
                </div>
              )}
            </div>

            {/* Related Contacts */}
            {client.relatedContacts.length > 0 && (
              <div>
                <div style={{ fontWeight: 500, marginBottom: '8px' }}>
                  Related Contacts ({client.relatedContacts.length})
                </div>
                {client.relatedContacts.map(renderContact)}
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '8px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--tl-border)' }}>
              {isDataverseConfigured() && (
                <Button
                  as="a"
                  appearance="subtle"
                  icon={<OpenRegular />}
                  size="small"
                  href={getDataverseRecordUrl('account', client.accountId) ?? undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open Account in Dataverse
                </Button>
              )}
            </div>
          </div>
        </AccordionPanel>
      </AccordionItem>
    );
  };

  if (isLoading) {
    return <TableSkeleton />;
  }

  if (error) {
    return (
      <div className="error-container">
        <p>Failed to load in-house client status. Please try again.</p>
        <Button onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h2>In-House Client Status</h2>
        <p>Track tax season readiness for bookkeeping, payroll, and sales tax clients</p>
      </div>

      {/* Stats Cards */}
      {data && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: tokens.fontSizeHero700, fontWeight: 600, color: '#0078d4' }}>{data.totalAccounts}</div>
            <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>Total Accounts</div>
          </div>
          <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: tokens.fontSizeHero700, fontWeight: 600, color: '#10B981' }}>{data.accountsSetUp}</div>
            <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>Accounts Set Up</div>
          </div>
          <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: tokens.fontSizeHero700, fontWeight: 600, color: '#EF4444' }}>{data.accountsMissing}</div>
            <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>Accounts Missing</div>
          </div>
          <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: tokens.fontSizeHero700, fontWeight: 600, color: '#0078d4' }}>{data.totalContacts}</div>
            <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>Total Contacts</div>
          </div>
          <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: tokens.fontSizeHero700, fontWeight: 600, color: '#10B981' }}>{data.contactsSetUp}</div>
            <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>Contacts Set Up</div>
          </div>
          <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: tokens.fontSizeHero700, fontWeight: 600, color: '#EF4444' }}>{data.contactsMissing}</div>
            <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>Contacts Missing</div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="toolbar" style={{ marginBottom: '16px', flexWrap: 'wrap' }}>
        <Dropdown
          value={selectedTaxYear.toString()}
          onOptionSelect={(_, d) => setSelectedTaxYear(Number(d.optionValue))}
          style={{ minWidth: '120px' }}
        >
          <Option text={`TY ${currentYear - 1}`} value={(currentYear - 1).toString()}>TY {currentYear - 1}</Option>
          <Option text={`TY ${currentYear - 2}`} value={(currentYear - 2).toString()}>TY {currentYear - 2}</Option>
        </Dropdown>

        <Dropdown
          value={filterStatus}
          onOptionSelect={(_, d) => setFilterStatus(d.optionValue as 'all' | 'missing' | 'setup')}
          style={{ minWidth: '160px' }}
        >
          <Option text="All Clients" value="all">All Clients</Option>
          <Option text="Needs Attention" value="missing">Needs Attention ({data?.clients.filter(c => !c.accountTaxPrepStatus.isSetUp || c.relatedContacts.some(r => !r.taxPrepStatus.isSetUp)).length || 0})</Option>
          <Option text="All Set Up" value="setup">All Set Up</Option>
        </Dropdown>

        <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
          <Button
            appearance="subtle"
            icon={allVisibleSelected ? <CheckboxCheckedRegular /> : <CheckboxUncheckedRegular />}
            onClick={toggleSelectAll}
            disabled={filteredClients.length === 0}
          >
            {allVisibleSelected ? 'Deselect All' : 'Select All'}
          </Button>

          {bulkOutreachHref ? (
            <Button as="a" appearance="primary" icon={<SendRegular />} {...realLinkProps(bulkOutreachHref, { navigate })}>
              Send Outreach ({selectedClients.size})
            </Button>
          ) : (
            <Button appearance="primary" icon={<SendRegular />} disabled>
              Send Outreach (0)
            </Button>
          )}

          <Button
            appearance="subtle"
            icon={<ArrowClockwiseRegular />}
            onClick={() => refetch()}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Client List */}
      <div className="card" style={{ padding: '16px' }}>
        {filteredClients.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--tl-text-secondary)' }}>
            {filterStatus === 'missing'
              ? 'All in-house clients are set up for tax season!'
              : 'No in-house clients found.'}
          </div>
        ) : (
          <Accordion multiple collapsible>
            {filteredClients.map(renderClient)}
          </Accordion>
        )}
      </div>
    </div>
  );
}
